import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable, distinctUntilChanged, map, shareReplay,
  switchMap,
} from 'rxjs';

import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { LocalStorageService } from '@sondermind/local-storage';
import { IInsurance } from '@sondermind/utilities/models-provider-network-company';

import { EmptyURIValue } from '../models/emptyURIValue';
import { IGender } from '../models/gender';
import { IModality } from '../models/modality';
import { ISpecialty } from '../models/specialty';
import { FilterOptionsHttpService } from '../services/filter-options.httpservice';

@Injectable({
  providedIn: 'root'
})
export class FilterOptionsStore {
  private genders$!: Observable<IGender[]>;
  private insurances$!: Observable<IInsurance[]>;
  private specialties$!: Observable<ISpecialty[]>;
  private modalities$!: Observable<IModality[]>;
  private currentStateSubject = new BehaviorSubject<string | null>(null);
  private isLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isStateLoadedSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private readonly localStorageKey = 'filter-options-current-state';
  private lastFetchedProviderNetworkState: string = '';

  constructor(
    private filtersHttpService: FilterOptionsHttpService,
    private localStorageService: LocalStorageService,
    private insuranceStore: ProviderNetworkCompanyStore
  ){
    const storedState = localStorageService.get(this.localStorageKey);
    if (storedState) {
      this.currentStateSubject.next(storedState);
      this.isStateLoadedSubject.next(true);
    } else {
      this.isStateLoadedSubject.next(false);
    }
  }

  setCurrentState(state: string | null): void {
    if (state && state === EmptyURIValue) return;

    this.currentStateSubject.next(state);
    if (state !== null) {
      this.localStorageService.set(this.localStorageKey, state);
      this.isStateLoadedSubject.next(true);
    } else {
      this.localStorageService.removeItem(this.localStorageKey);
      this.isStateLoadedSubject.next(false);
    }
  }

  getCurrentState(): string | null {
    return this.currentStateSubject.value;
  }

  setIsLoaded(loaded: boolean): void {
    this.isLoadedSubject.next(loaded);
  }

  isLoaded$(): Observable<boolean> {
    return this.isLoadedSubject.asObservable();
  }

  isStateLoaded$(): Observable<boolean | null> {
    return this.isStateLoadedSubject.asObservable();
  }

  currentState$(): Observable<string | null> {
    return this.currentStateSubject.asObservable();
  }

  genderList$(): Observable<IGender[]> {
    if (!this.genders$) {
      this.genders$ = this.filtersHttpService.listGenders$().pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.genders$;
  }

  insuranceList$(): Observable<IInsurance[]> {
    return this.currentStateSubject.pipe(
      distinctUntilChanged(),
      switchMap((state) => {
        if (state && (!this.insurances$ || this.lastFetchedProviderNetworkState !== state)) {
          this.lastFetchedProviderNetworkState = state;
          this.insurances$ = this.insuranceStore.getInsuranceOptionsByState$(state);
        }

        return this.insurances$;
      })
    );
  }

  specialtyList$(): Observable<ISpecialty[]> {
    if (!this.specialties$) {
      this.specialties$ = this.filtersHttpService.listSpecialties$({ page: 'disabled' }).pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.specialties$;
  }

  modalityList$(): Observable<IModality[]> {
    if (!this.modalities$) {
      this.modalities$ = this.filtersHttpService.listModalities$().pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.modalities$;
  }
}
